import { motion } from 'framer-motion';
import { Calendar, Clock, Eye, ArrowLeft, Share2, Twitter, Facebook, Linkedin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import AuthorAvatar from '../components/AuthorAvatar';
import BlogMarkdown from '../components/BlogMarkdown';
import Breadcrumb from '../components/Breadcrumb';
import LatestBlogsSidebar from '../components/LatestBlogsSidebar';
import { blogApi, BlogPost } from '../services/api';

// Using real API calls now

export default function BlogPostPage() {
  const { slug } = useParams();
  const _navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadPost(slug);
    }
  }, [slug]);

  const loadPost = async (postSlug: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await blogApi.public.getPostBySlug(postSlug);

      if (response.success && response.data) {
        setPost(response.data);

        // Update page title and meta description
        document.title = response.data.seoTitle || response.data.title;

        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription && response.data.seoDescription) {
          metaDescription.setAttribute('content', response.data.seoDescription);
        }
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      console.error('Error loading blog post:', err);
      setError('Failed to load blog post');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = post?.seoDescription || post?.title || 'Check out this interesting blog post';

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'copy':
        navigator.clipboard.writeText(url).then(() => {
          // Could add a toast notification here
          alert('Link copied to clipboard!');
        });
        break;
    }
  };

  if (isLoading) {
    return (
      <MarketingLayout>
        <section
          className="public-section min-h-96 flex flex-col gap-4 items-center justify-center"
          role="status"
          aria-label="Loading article"
        >
          <div
            className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
          <p className="text-gray-600">Loading your next strategic insight…</p>
        </section>
      </MarketingLayout>
    );
  }

  if (error || !post) {
    return (
      <MarketingLayout>
        <section className="public-section min-h-96 flex items-center justify-center px-5">
          <div className="public-card text-center p-8 md:p-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Post Not Found</h1>
            <p className="text-gray-600 mb-4">
              {error || "The blog post you're looking for doesn't exist."}
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Blog</span>
            </Link>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <div className="public-breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Blog', path: '/blog' }, { label: post.title }]} />
        </div>
      </div>

      {/* Main Content */}
      <section className="public-section">
        <div className="container mx-auto px-4">
          {/* Back to Blog Link */}
          <Link
            to="/blog"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Blog</span>
          </Link>

          {/* Content Grid with Sidebar */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Article */}
            <article className="public-card public-card--white flex-1 min-w-0 overflow-hidden">
              <header className="p-6 md:p-10">
                <div className="flex flex-wrap gap-2 mb-5">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag.slug}
                      to={`/blog?tag=${tag.slug}`}
                      className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
                <motion.h1
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
                >
                  {post.title}
                </motion.h1>
                {post.excerpt && (
                  <p className="text-lg text-gray-600 mt-5 leading-relaxed">{post.excerpt}</p>
                )}
              </header>
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-56 md:h-80 object-cover"
              />

              {/* Article Content */}
              <div className="p-6 md:p-10">
                {/* Author and Meta Info */}
                <div className="flex flex-wrap items-center justify-between gap-5 pb-6 mb-8 border-b border-gray-200">
                  <div className="flex items-center space-x-4">
                    <AuthorAvatar
                      photo={post.author.photo}
                      name={post.author.name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{post.author.name}</p>
                      <p className="text-sm text-gray-500">{post.author.title}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(post.publishedAt)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{post.readingTimeMinutes} min read</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye className="h-4 w-4" />
                      <span>{post.viewCount} views</span>
                    </div>
                  </div>
                </div>

                {/* Article Actions */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                  <p className="text-sm font-medium text-gray-600">Share this article</p>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => handleShare('twitter')}
                      className="flex items-center space-x-2 text-gray-600 hover:text-blue-400 transition-colors"
                      title="Share on Twitter"
                    >
                      <Twitter className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
                      title="Share on Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="flex items-center space-x-2 text-gray-600 hover:text-blue-700 transition-colors"
                      title="Share on LinkedIn"
                    >
                      <Linkedin className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors"
                      title="Copy Link"
                    >
                      <Share2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Article Content */}
                <motion.div
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="public-prose prose prose-lg max-w-none"
                >
                  <BlogMarkdown content={post.content} />
                </motion.div>
              </div>
            </article>

            {/* Sidebar */}
            <LatestBlogsSidebar currentPostId={post.id} />
          </div>

          {/* Related Posts or Call to Action */}
          <div className="public-cta mt-12 text-center">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              Put these ideas to work in your company.
            </h3>
            <p className="text-gray-600 mb-6">
              Move from useful insight to a connected plan. Bring your vision, priorities, people
              and projects into one shared workspace.
            </p>
            <Link to="/sign-up" className="public-button public-button--primary">
              <span>Build my strategic plan</span>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
